import { DASHBOARD } from '@constants/internal-route.constant';
import { useAuth } from '@context/Auth';
import { usePermissions } from '@services/helper/permission.helper';
import { menus, topItems } from 'layouts/components/sidebar/menu-config';
import { Navigate } from 'react-router-dom';

/** Every sidebar screen, in sidebar order: the Dashboard, then each group's pages. */
const screens = () => [...topItems, ...menus.flatMap((g) => g.children)];

/** The screen a feature code opens, read off the sidebar (stock keys screens by code, not url). */
const routeOf = (code: string) => screens().find((m) => m.route_code === code)?.to;

/**
 * Where the app root ('/') and the portal hand-off ('auth/*') send a signed-in user:
 *   1. the landing page set for their role IN THIS APP on portal's Role Permission screen (verify-token
 *      `landing`, resolved server-side for this app's aud), when they can open it, else
 *   2. the Dashboard, when their role is granted it (DASHBOARD on Role Permission), else
 *   3. the first screen their role is granted, in sidebar order, else
 *   4. the Dashboard, which then shows Unauthorized: the role holds nothing in this app.
 *
 * It waits for verify-token, because until it answers `landing` and the grants are the previous
 * session's copy from storage. Only these two entry points use it, so a landing page that was later
 * revoked can never bounce with a route guard.
 */
const LandingRedirect = () => {
  const { isChecking, userInfo } = useAuth();
  const { canSee } = usePermissions();
  if (isChecking) return null;

  const code = userInfo?.landing?.code;
  const landing = code && canSee(code) ? routeOf(code) : undefined;
  const firstGranted = screens().find((m) => m.route_code && canSee(m.route_code))?.to;
  return <Navigate to={landing || firstGranted || DASHBOARD} replace />;
};

export default LandingRedirect;
